import type { RefObject } from 'react';
import { MailCheck } from 'lucide-react';
import DoneIcon from '../icons/done-icon';
import SettingsIcon from '../icons/settings-icon';
import type {
  HeaderNotificationItem as HeaderNotificationItemData,
  HeaderNotificationSource,
} from './notification-types';
import { HeaderNotificationItem } from './notification-item';
import { NotificationEmptyState } from './notification-empty-state';
import { NotificationInfiniteScrollTrigger } from './notification-infinite-scroll-trigger';

type NotificationFeedProps = {
  items: HeaderNotificationItemData[];
  groups?: { id: string; name: string }[];
  source?: HeaderNotificationSource;
  scrollRootRef: RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  onMarkAllAsRead: () => void;
  onOpenPreferences: () => void;
  onSelectNotification: (item: HeaderNotificationItemData) => void;
};

const DEFAULT_GROUPS = [
  { id: 'today', name: 'Hôm nay' },
  { id: 'last_7_days', name: '7 ngày qua' },
  { id: 'older', name: 'Cũ hơn' },
];

export function NotificationFeed({
  items,
  groups,
  source,
  scrollRootRef,
  isMobile,
  onMarkAllAsRead,
  onOpenPreferences,
  onSelectNotification,
}: NotificationFeedProps) {
  const visibleGroups = groups?.length ? groups : DEFAULT_GROUPS;
  const isUnreadStateKnown = source ? source.totalUnread !== null : true;
  const hasUnread = source
    ? (source.totalUnread ?? 0) > 0
    : items.some(item => item.unread);
  const groupIds = new Set(visibleGroups.map(group => group.id));
  const allGroups = [
    ...visibleGroups,
    ...Array.from(new Set(items.map(item => item.group)))
      .filter(id => !groupIds.has(id))
      .map(id => ({ id, name: id })),
  ];

  return (
    <section
      aria-label={isMobile ? 'Hoạt động' : undefined}
      aria-labelledby={isMobile ? undefined : 'notification-feed-title'}
    >
      {!isMobile ? (
        <header className="mb-5 flex items-center justify-between gap-3 px-2">
          <h2
            id="notification-feed-title"
            className="font-display text-display-2xs leading-display-2xs xl:text-display-xs xl:leading-display-xs font-semibold"
          >
            Hoạt động
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onMarkAllAsRead}
              disabled={source?.isMutating || !isUnreadStateKnown || !hasUnread}
              className="text-on-surface hover:text-primary focus-visible:ring-primary/50 leading-xs flex items-center gap-1 text-xs font-semibold focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              {isUnreadStateKnown && !hasUnread ? (
                <DoneIcon size={20} aria-hidden />
              ) : (
                <MailCheck size={20} aria-hidden />
              )}
              {!isUnreadStateKnown
                ? 'Đang kiểm tra…'
                : hasUnread
                  ? 'Đánh dấu tất cả là đã đọc'
                  : 'Đã đọc tất cả'}
            </button>
            <button
              type="button"
              onClick={onOpenPreferences}
              aria-label="Cài đặt thông báo"
              className="text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 flex size-8 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:outline-none"
            >
              <SettingsIcon size={24} aria-hidden />
            </button>
          </div>
        </header>
      ) : null}

      {source?.actionError ? (
        <p role="alert" className="text-negative mb-3 px-2 text-xs">
          {source.actionError}
        </p>
      ) : null}

      {source?.isLoading && items.length === 0 ? (
        <p className="text-on-surface-variant px-2 py-16 text-center text-sm">
          Đang tải thông báo…
        </p>
      ) : source?.error && items.length === 0 ? (
        <div className="px-2 py-16 text-center">
          <p role="alert" className="text-negative mb-3 text-sm">
            {source.error}
          </p>
          <button
            type="button"
            onClick={source.onRetry}
            className="text-primary focus-visible:ring-primary/50 rounded-lg px-3 py-2 text-sm font-semibold focus-visible:ring-2 focus-visible:outline-none"
          >
            Thử lại
          </button>
        </div>
      ) : items.length === 0 ? (
        <NotificationEmptyState />
      ) : (
        <div>
          {allGroups.map(group => {
            const groupItems = items.filter(item => item.group === group.id);

            if (groupItems.length === 0) return null;

            return (
              <section key={group.id} className="mt-6 first:mt-0">
                <h3 className="leading-ui-base mb-2 px-2 text-[17px] font-semibold">
                  {group.name}
                </h3>
                <div className="space-y-1">
                  {groupItems.map(item => (
                    <HeaderNotificationItem
                      key={item.id}
                      item={item}
                      isMutating={source?.isMutating}
                      onSelect={onSelectNotification}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {source ? (
            <NotificationInfiniteScrollTrigger
              rootRef={scrollRootRef}
              hasMore={source.hasMore}
              isLoading={source.isLoadingMore}
              isBlocked={source.isMutating}
              error={source.error}
              onLoadMore={source.onLoadMore}
              onRetry={source.onRetry}
            />
          ) : null}

          {!source?.hasMore && !source?.isLoadingMore ? (
            <p className="text-on-surface-variant leading-xs mt-5 px-2 text-center text-xs">
              Đã xem hết. Cùng Upzi tìm việc mới nào!
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
